import {
  ClosedTrades,
  KPIDailyValuationDetailsData,
  TradeClassificationData,
} from '@/pages/TradeCenter/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Trade, UserTrades } from 'trade';

import { CloseOutTradesResultsPayload } from './actions';

export interface TradeCenterState {
  trades: {
    data: Trade[];
    loading: boolean;
  };
  activeTrades: {
    data: Trade[];
    loading: boolean;
  };
  pendingTrades: {
    data: Trade[];
    loading: boolean;
  };
  downloadPortfolioStatementReportLoading: boolean;
  downloadValuationReportLoading: boolean;

  bulkClosedTrades: {
    data: CloseOutTradesResultsPayload[];
    loading: boolean;
  };
  pendingClosedTrades: {
    data: ClosedTrades[];
    loading: boolean;
  };
  closedTrades: {
    data: ClosedTrades[];
    loading: boolean;
  };
  reviewClosedTradesLoading: boolean;

  tradeClassData: {
    data: TradeClassificationData;
    loading: boolean;
  };
  setTradeClassDataLoading: boolean;

  userTrades: {
    data: UserTrades[];
    loading: boolean;
  };

  kpiDailyValuationDetails: {
    loading: boolean;
    data: KPIDailyValuationDetailsData;
  };
}

const initialState: TradeCenterState = {
  trades: {
    data: [],
    loading: false,
  },
  activeTrades: {
    data: [],
    loading: false,
  },
  pendingTrades: {
    data: [],
    loading: false,
  },
  downloadPortfolioStatementReportLoading: false,
  downloadValuationReportLoading: false,

  bulkClosedTrades: {
    data: [],
    loading: false,
  },
  pendingClosedTrades: {
    data: [],
    loading: false,
  },
  closedTrades: {
    data: [],
    loading: false,
  },
  reviewClosedTradesLoading: false,

  tradeClassData: {
    data: {
      trade_classification_id: null,
      trade_classification: null,
      trade_type: null,
    },
    loading: false,
  },
  setTradeClassDataLoading: false,

  userTrades: {
    data: [],
    loading: false,
  },
  kpiDailyValuationDetails: {
    loading: false,
    data: {
      valued_trades: null,
      unvalued_trades: null,
      valued_cva: null,
      unvalued_cva: null,
    },
  },
};

const tradeCenterSlice = createSlice({
  name: 'tradeCenter',
  initialState,
  reducers: {
    // All trades
    getAllTradesRequest: (state) => {
      state.trades.loading = true;
    },
    getAllTradesSuccess: (state, action: PayloadAction<{ data: Trade[] }>) => {
      state.trades.data = action.payload.data;
      state.trades.loading = false;
    },
    getAllTradesFailure: (state) => {
      state.trades.loading = false;
    },

    // Active trades
    getActiveTradesRequest: (state) => {
      state.activeTrades.loading = true;
    },
    getActiveTradesSuccess: (
      state,
      action: PayloadAction<{ data: Trade[] }>,
    ) => {
      state.activeTrades.data = action.payload.data;
      state.activeTrades.loading = false;
    },
    getActiveTradesFailure: (state) => {
      state.activeTrades.loading = false;
    },

    // Pending trades
    getPendingTradesRequest: (state) => {
      state.pendingTrades.loading = true;
    },
    getPendingTradesSuccess: (
      state,
      action: PayloadAction<{ data: Trade[] }>,
    ) => {
      state.pendingTrades.data = action.payload.data;
      state.pendingTrades.loading = false;
    },
    getPendingTradesFailure: (state) => {
      state.pendingTrades.loading = false;
    },

    // Portfolio statement download
    downloadPortfolioRequest: (state) => {
      state.downloadPortfolioStatementReportLoading = true;
    },
    downloadPortfolioSuccess: (state) => {
      state.downloadPortfolioStatementReportLoading = false;
    },
    downloadPortfolioFailure: (state) => {
      state.downloadPortfolioStatementReportLoading = false;
    },

    // Valuation report download
    downloadValuationRequest: (state) => {
      state.downloadValuationReportLoading = true;
    },
    downloadValuationSuccess: (state) => {
      state.downloadValuationReportLoading = false;
    },
    downloadValuationFailure: (state) => {
      state.downloadValuationReportLoading = false;
    },

    // Bulk close trades
    bulkCloseOutTradesRequest: (state) => {
      state.bulkClosedTrades.loading = true;
    },
    bulkCloseOutTradesSuccess: (
      state,
      action: PayloadAction<{ data: CloseOutTradesResultsPayload[] }>,
    ) => {
      state.bulkClosedTrades.data = action.payload.data;
      state.bulkClosedTrades.loading = false;
    },
    bulkCloseOutTradesFailure: (state) => {
      state.bulkClosedTrades.loading = false;
    },

    // Pending closed trades
    getPendingApprovalClosedTradesRequest: (state) => {
      state.pendingClosedTrades.loading = true;
    },
    getPendingApprovalClosedTradesSuccess: (
      state,
      action: PayloadAction<{ data: ClosedTrades[] }>,
    ) => {
      state.pendingClosedTrades.data = action.payload.data;
      state.pendingClosedTrades.loading = false;
    },
    getPendingApprovalClosedTradesFailure: (state) => {
      state.pendingClosedTrades.loading = false;
    },

    // Closed trades
    getClosedOutTradesRequest: (state) => {
      state.closedTrades.loading = true;
    },
    getClosedOutTradesSuccess: (
      state,
      action: PayloadAction<{ data: ClosedTrades[] }>,
    ) => {
      state.closedTrades.data = action.payload.data;
      state.closedTrades.loading = false;
    },
    getClosedOutTradesFailure: (state) => {
      state.closedTrades.loading = false;
    },

    // Review closed trades
    reviewCloseTradesRequest: (state) => {
      state.reviewClosedTradesLoading = true;
    },
    reviewCloseTradesSuccess: (state) => {
      state.reviewClosedTradesLoading = false;
    },
    reviewCloseTradesFailure: (state) => {
      state.reviewClosedTradesLoading = false;
    },

    // Trade class data
    getTradeClassTagRequest: (state) => {
      state.tradeClassData.loading = true;
    },
    getTradeClassTagSuccess: (
      state,
      action: PayloadAction<{ data: TradeClassificationData }>,
    ) => {
      state.tradeClassData.data = action.payload.data;
      state.tradeClassData.loading = false;
    },
    getTradeClassTagFailure: (state) => {
      state.tradeClassData.loading = false;
    },

    // Set trade class data
    setTradeClassDataRequest: (state) => {
      state.setTradeClassDataLoading = true;
    },
    setTradeClassDataSuccess: (state) => {
      state.setTradeClassDataLoading = false;
    },
    setTradeClassDataFailure: (state) => {
      state.setTradeClassDataLoading = false;
    },

    // User trades
    getUserTradesRequest: (state) => {
      state.userTrades.loading = true;
    },
    getUserTradesSuccess: (
      state,
      action: PayloadAction<{ data: UserTrades[] }>,
    ) => {
      state.userTrades.data = action.payload.data;
      state.userTrades.loading = false;
    },
    getUserTradesFailure: (state) => {
      state.userTrades.loading = false;
    },

    // fetch kpi daily valuation details
    getKPIDailyValuationDetailsRequest: (state) => {
      state.kpiDailyValuationDetails.loading = true;
    },

    getKPIDailyValuationDetailsSuccess: (
      state,
      action: PayloadAction<{ data: KPIDailyValuationDetailsData }>,
    ) => {
      state.kpiDailyValuationDetails.data = action.payload.data;
      state.kpiDailyValuationDetails.loading = false;
    },

    getKPIDailyValuationDetailsFailure: (state) => {
      state.kpiDailyValuationDetails.loading = false;
    },
  },
});

export const tradeCenterActions = tradeCenterSlice.actions;
export default tradeCenterSlice.reducer;
